import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { productGroupsAPI, API_URL } from "../../utils/api";
import "./ProductListing.modern.css";

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const ProductSkeleton = () => (
  <div className="modern-product-card skeleton">
    <div className="skeleton-image"></div>
    <div className="skeleton-text"></div>
    <div className="skeleton-text short"></div>
    <div className="skeleton-text"></div>
  </div>
);

const ModernProductListing = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const gridRef = useRef(null);
  const loadMoreRef = useRef(null);
  const [showBackToTop, setShowBackToTop] = useState(false);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'best-selling');
  const [selectedBrands, setSelectedBrands] = useState(searchParams.getAll('brand') || []);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [priceRange, setPriceRange] = useState([
    searchParams.get('priceMin') || '',
    searchParams.get('priceMax') || ''
  ]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [limit] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [allBrands, setAllBrands] = useState([]);
  const [allCategories, setAllCategories] = useState([]);

  const debouncedSearch = useDebounce(searchTerm, 400);

  const mapSort = {
    'best-selling': 'featured',
    'price-low': 'priceAsc',
    'price-high': 'priceDesc',
    'name-az': 'nameAz'
  };

  const hasMore = page < totalPages;

  const scrollToTop = useCallback(() => {
    gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!loadMoreRef.current ||!hasMore || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPage((p) => p + 1);
        }
      },
      { rootMargin: '400px' }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore]);

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const res = await productGroupsAPI.getProductGroupsMeta();
        const data = res.data?.data || {};
        setAllBrands(data.brands || []);
        setAllCategories(data.categories || []);
      } catch (err) {
        console.error("Error fetching meta:", err);
      }
    };
    fetchMeta();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (selectedCategory) params.set('category', selectedCategory);
    if (selectedBrands.length) selectedBrands.forEach(b => params.append('brand', b));
    if (priceRange[0]) params.set('priceMin', priceRange[0]);
    if (priceRange[1]) params.set('priceMax', priceRange[1]);
    if (sortBy!== 'best-selling') params.set('sort', sortBy);
    if (page > 1) params.set('page', page);
    setSearchParams(params, { replace: true });
  }, [debouncedSearch, selectedBrands, selectedCategory, priceRange, sortBy, page, setSearchParams]);

  useEffect(() => {
    setPage(1);
    setProducts([]);
    scrollToTop();
  }, [debouncedSearch, selectedBrands, selectedCategory, priceRange, sortBy, scrollToTop]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        page === 1? setLoading(true) : setLoadingMore(true);
        const offset = (page - 1) * limit;

        const isValid = (v) => v!== null && v!== undefined && v!== 'undefined' && v!== '';
        const cleanBrands = selectedBrands.filter(isValid);
        const cleanCategory = isValid(selectedCategory)? selectedCategory.trim() : undefined;
        const cleanSearch = isValid(debouncedSearch)? debouncedSearch.trim() : undefined;

        const minNum = Number(priceRange[0]);
        const maxNum = Number(priceRange[1]);
        const priceMin = priceRange[0]!== '' &&!Number.isNaN(minNum)? minNum : undefined;
        const priceMax = priceRange[1]!== '' &&!Number.isNaN(maxNum)? maxNum : undefined;

        const params = {
          limit,
          offset,
          sort: mapSort[sortBy] || 'featured'
        };

        if (cleanBrands.length > 0) params.vendor = cleanBrands.join(',');
        if (cleanCategory) params.category = cleanCategory;
        if (cleanSearch) params.search = cleanSearch;
        if (priceMin!== undefined) params.priceMin = priceMin;
        if (priceMax!== undefined) params.priceMax = priceMax;

        const res = await productGroupsAPI.getProductGroups(params);
        const data = res.data?.data || [];
        const total = res.data?.count || 0;

        setProducts(prev => page === 1? data : [...prev,...data]);
        setTotalItems(total);
        setTotalPages(Math.ceil(total / limit));
      } catch (err) {
        console.error("Error fetching data:", err);
        if (page === 1) setProducts([]);
        setTotalItems(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };

    fetchData();
  }, [page, limit, selectedBrands, selectedCategory, debouncedSearch, priceRange, sortBy]);

  useEffect(() => {
    const fetchTrending = async () => {
      try {
        const res = await productGroupsAPI.getTrendingSuggestions();
        const data = res.data?.data || [];
        setTrendingProducts(data);
      } catch (err) {
        console.error("Error fetching trending products:", err);
      }
    };
    fetchTrending();
  }, []);

  const getImageUrl = (imgPath) => {
    if (!imgPath) return null;
    if (imgPath.startsWith('http')) return imgPath;
    const baseUrl = API_URL.replace('/api', '');
    if (imgPath.startsWith('/uploads/')) return `${baseUrl}${imgPath}`;
    return `${baseUrl}/api/images/${imgPath}`;
  };

  const toggleBrand = (brand) => {
    setSelectedBrands(prev =>
      prev.includes(brand)? prev.filter(b => b!== brand) : [...prev, brand]
    );
  };

  const handleQuickAdd = (e, product) => {
    e.stopPropagation();
    navigate(`/product-group/${product.group_id}`);
    return;
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedBrands([]);
    setPriceRange(['', '']);
    setSearchTerm('');
  };

  const hasActiveFilters = selectedCategory || selectedBrands.length > 0 || priceRange[0]!== '' || priceRange[1]!== '' || searchTerm;

  if (loading && products.length === 0) {
    return (
      <div className="modern-shop-page">
        <div className="shop-header">
          <div className="shop-title-section">
            <h1 className="shop-title">All Products</h1>
            <p className="shop-count">Loading...</p>
          </div>
        </div>
        <div className="products-grid">
          {Array.from({ length: 12 }).map((_, i) => <ProductSkeleton key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="modern-shop-page">
      <div className="breadcrumbs">
        <span onClick={() => navigate('/')} className="breadcrumb-link">Home</span>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">All Products</span>
      </div>

      <div className="shop-header">
        <div className="shop-title-section">
          <h1 className="shop-title">All Products</h1>
          <p className="shop-count">{totalItems} items</p>
        </div>
        <div className="shop-controls">
          <div className="shop-search-bar">
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="shop-search-input"
            />
            {searchTerm && (
              <button className="shop-search-clear" onClick={() => setSearchTerm('')}>×</button>
            )}
          </div>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select">
            <option value="best-selling">Best Selling</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="name-az">Name: A-Z</option>
          </select>
          <button className="mobile-filter-btn" onClick={() => setIsFilterOpen(true)}>
            Filters
          </button>
        </div>
      </div>

      {trendingProducts?.length > 0 && (
        <section className="trending-products-section">
          <h2>Trending Products</h2>
          <div className="trending-products-grid">
            {trendingProducts.map(product => (
              <div key={product.product_id} className="trending-product-card" onClick={() => navigate(`/product-group/${product.product_id}`)}>
                <img
                  src={getImageUrl(product.hero_image)}
                  alt={product.title}
                  onError={(e) => { e.target.src = '/images/placeholder.webp'; }}
                />
                <p className="trending-product-title">{product.title}</p>
                <p className="trending-product-price">
                  ${product.min_price || 0}{product.max_price && product.max_price!== product.min_price? ` - ${product.max_price}` : ''}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {isFilterOpen && (
        <div className="filter-modal-overlay" onClick={() => setIsFilterOpen(false)}>
          <div className="filter-modal" onClick={(e) => e.stopPropagation()}>
            <div className="filter-modal-header">
              <h3>Filters</h3>
              <button className="close-btn" onClick={() => setIsFilterOpen(false)}>×</button>
            </div>
            <div className="filter-modal-content">
              <div className="filter-section">
                <h4>Brand</h4>
                {allBrands.map(brand => (
                  <label key={brand.id || brand.name}>
                    <input
                      type="checkbox"
                      checked={selectedBrands.includes(brand.name)}
                      onChange={() => toggleBrand(brand.name)}
                    />
                    <span>{brand.name}</span>
                  </label>
                ))}
              </div>
              <div className="filter-section">
                <h4>Category</h4>
                {allCategories.map(cat => (
                  <label key={cat.id}>
                    <input
                      type="checkbox"
                      checked={selectedCategory === cat.slug}
                      onChange={() => setSelectedCategory(selectedCategory === cat.slug? '' : cat.slug)}
                    />
                    <span>{cat.name}</span>
                  </label>
                ))}
              </div>
              <div className="filter-section">
                <h4>Price</h4>
                <input className="shop-search-input filter-price"
                  placeholder="Min"
                  value={priceRange[0]}
                  onChange={(e) => setPriceRange([e.target.value, priceRange[1]])}
                />
                <input className="shop-search-input filter-price"
                  placeholder="Max"
                  value={priceRange[1]}
                  onChange={(e) => setPriceRange([priceRange[0], e.target.value])}
                />
              </div>
            </div>
            <div className="filter-modal-footer">
              <button onClick={clearFilters}>Clear</button>
              <button onClick={() => setIsFilterOpen(false)}>Apply</button>
            </div>
          </div>
        </div>
      )}

      {hasActiveFilters && (
        <div className="active-filters">
          <span className="active-filters-label">Active filters:</span>
          {searchTerm && (
            <span className="filter-chip">
              Search: {searchTerm}
              <button onClick={() => setSearchTerm('')}>×</button>
            </span>
          )}
          {selectedBrands.map(brand => (
            <span key={brand} className="filter-chip">
              Brand: {brand}
              <button onClick={() => toggleBrand(brand)}>×</button>
            </span>
          ))}
          {selectedCategory && (
            <span className="filter-chip">
              Category: {allCategories.find(c => c.slug === selectedCategory)?.name || selectedCategory}
              <button onClick={() => setSelectedCategory('')}>×</button>
            </span>
          )}
          {(priceRange[0]!== '' || priceRange[1]!== '') && (
            <span className="filter-chip">
              Price: {priceRange[0]!== ''? `$${priceRange[0]}` : '$0'} - {priceRange[1]!== ''? `$${priceRange[1]}` : '∞'}
              <button onClick={() => setPriceRange(['', ''])}>×</button>
            </span>
          )}
          <button className="clear-all-btn" onClick={clearFilters}>Clear all</button>
        </div>
      )}

      <div className="shop-content-with-sidebar">
        <aside className="filters-sidebar">
          <div className="filters-sidebar-header">
            <h3>Filters</h3>
            {hasActiveFilters && (
              <button className="clear-all-sidebar-btn" onClick={clearFilters}>Clear all</button>
            )}
          </div>
          <div className="filter-section">
            <h4 className="filter-section-title">Brand</h4>
            <div className="filter-options">
              {allBrands.map(brand => (
                <label key={brand.id || brand.name} className="filter-option">
                  <input
                    type="checkbox"
                    checked={selectedBrands.includes(brand.name)}
                    onChange={() => toggleBrand(brand.name)}
                  />
                  <span>{brand.name}</span>
                </label>
              ))}
            </div>
          </div>
          {allCategories.length > 0 && (
            <div className="filter-section">
              <h4 className="filter-section-title">Category</h4>
              <div className="filter-options">
                {allCategories.map(cat => (
                  <label key={cat.id} className="filter-option">
                    <input
                      type="checkbox"
                      checked={selectedCategory === cat.slug}
                      onChange={() => setSelectedCategory(selectedCategory === cat.slug? '' : cat.slug)}
                    />
                    <span>{cat.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="filter-section">
            <h4 className="filter-section-title">Price Range</h4>
            <div className="price-range-inputs">
              <input type="text" inputMode="decimal" value={priceRange[0]} onChange={(e) => setPriceRange([e.target.value, priceRange[1]])} className="price-input" placeholder="Min" />
              <span className="price-separator">to</span>
              <input type="text" inputMode="decimal" value={priceRange[1]} onChange={(e) => setPriceRange([priceRange[0], e.target.value])} className="price-input" placeholder="Max" />
            </div>
          </div>
        </aside>

        <main className="products-main-content" ref={gridRef}>
          <div className="products-grid">
            {products.map((product) => (
              <div key={product.group_id} className="modern-product-card" onClick={() => navigate(`/product-group/${product.group_id}`)}>
                <div className="product-image-wrapper">
                  {(product.hero_image || product.preview_variants?.[0]?.image_url)? (
                    <img
                      src={getImageUrl(product.hero_image || product.preview_variants[0].image_url)}
                      alt={product.title}
                      className="product-image"
                      onError={(e) => { e.target.src = '/images/placeholder.webp'; }}
                      loading="lazy"
                    />
                  ) : (
                    <div className="no-image-placeholder">No Image</div>
                  )}
                  <button className="quick-add-button" onClick={(e) => handleQuickAdd(e, product)}>
                    {product.total_variants > 1? 'Select Options' : 'Select Options'}
                  </button>
                </div>
                <div className="product-card-info">
                  <p className="product-brand">{product.vendor_name || 'Premium'}</p>
                  <h3 className="product-card-name">{product.title}</h3>
                  <p className="product-category">{product.category_name || product.department_name || 'Uncategorized'}</p>
                  <div className="product-card-footer">
                    <p className="product-card-price">
                      ${product.min_price? Number(product.min_price).toFixed(2) : '0.00'}
                      {product.max_price && product.max_price!== product.min_price && (
                        <span> - ${Number(product.max_price).toFixed(2)}</span>
                      )}
                    </p>
                    {product.in_stock && (
                      <span className="stock-indicator">
                        <span className="stock-dot"></span>
                        In stock
                      </span>
                    )}
                  </div>
                  {product.total_variants > 1 && (
                    <p className="variant-count">{product.total_variants} options</p>
                  )}
                </div>
              </div>
            ))}
            {loadingMore && Array.from({ length: 4 }).map((_, i) => <ProductSkeleton key={`sk-${i}`} />)}
          </div>

          <div ref={loadMoreRef} style={{ height: '1px' }} />

          {products.length === 0 &&!loading && (
            <div className="no-products-message">
              <p>No products found matching your criteria</p>
              <button className="clear-filters-btn" onClick={clearFilters}>Clear filters</button>
            </div>
          )}

          {hasMore &&!loading && (
            <div className="load-more-wrapper">
              <button
                className="load-more-btn"
                onClick={() => setPage(p => p + 1)}
                disabled={loadingMore}
              >
                {loadingMore? 'Loading...' : `Load more (${products.length}/${totalItems})`}
              </button>
            </div>
          )}
        </main>
      </div>

      {showBackToTop && (
        <button className="back-to-top" onClick={scrollToTop}>
          ↑
        </button>
      )}
    </div>
  );
};

export default ModernProductListing;